import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Edit3,
  Eye,
  FileText,
  Film,
  Globe2,
  History,
  LayoutGrid,
  Link2,
  Loader2,
  MapPin,
  PlaySquare,
  Plus,
  Power,
  Radio,
  Search,
  SlidersHorizontal,
  Trash2,
  Type,
  UploadCloud,
  Users,
  X,
} from "lucide-react";
import { shortVideosService, buildShortVideoAssetUrl } from "../../../services/shortVideos.service";

const BORDER = "#f2c6cc";
const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";

const CONTEXT_OPTIONS = [
  { label: "Grocery", value: "GROCERY" },
  { label: "Food", value: "FOOD" },
  { label: "Grocery + Food", value: "ALL" },
];

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Active now", value: "ACTIVE" },
  { label: "Scheduled", value: "SCHEDULED" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Deleted", value: "DELETED" },
];

const PAGE_SIZE_OPTIONS = [
  { label: "10", value: "10" },
  { label: "20", value: "20" },
  { label: "50", value: "50" },
  { label: "100", value: "100" },
];


function toOptionValue(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeOptionList(list = []) {
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        return { label: String(item), value: toOptionValue(item) };
      }

      const rawValue =
        item?.value ??
        item?.id ??
        item?.warehouseId ??
        item?.warehouse_id ??
        item?.uuid ??
        item?.code ??
        "";

      const value = toOptionValue(rawValue);
      const label =
        item?.label ||
        item?.name ||
        item?.warehouseName ||
        item?.warehouse_name ||
        item?.title ||
        (value ? `Warehouse ${value}` : "");

      return { label: String(label || value), value };
    })
    .filter((item) => item.value)
    .filter((item) => {
      if (seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    });
}

function getVideoWarehouseIds(item) {
  const rawValues = [
    item?.warehouseId,
    item?.warehouse_id,
    item?.warehouse?.id,
    ...(Array.isArray(item?.warehouseIds) ? item.warehouseIds : []),
    ...(Array.isArray(item?.filters?.warehouseIds) ? item.filters.warehouseIds : []),
  ];

  return rawValues.map(toOptionValue).filter(Boolean);
}

const WEEK_DAYS = [
  { label: "Sun", value: "0" },
  { label: "Mon", value: "1" },
  { label: "Tue", value: "2" },
  { label: "Wed", value: "3" },
  { label: "Thu", value: "4" },
  { label: "Fri", value: "5" },
  { label: "Sat", value: "6" },
];

const today = toDateValue(new Date());

const emptyForm = {
  title: "",
  description: "",
  redirectType: "PRODUCT",
  productId: "",
  productVariantId: "",
  deepLink: "",
  ctaLabel: "Shop now",
  targetType: "ALL",
  filters: {
    contexts: ["GROCERY"],
    warehouseIds: [],
  },
  displayOrder: 100,
  isActive: true,
  startDate: today,
  endDate: today,
  dailyStartTime: "00:00",
  dailyEndTime: "23:59",
  daysOfWeek: ["0", "1", "2", "3", "4", "5", "6"],
  video: null,
  thumbnail: null,
  existingVideoUrl: "",
  existingThumbnailUrl: "",
};

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[24px] border bg-white shadow-sm ${className}`}
      style={{ borderColor: BORDER }}
    >
      {children}
    </div>
  );
}

function Pill({ children, tone = "slate" }) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-100",
    red: "bg-red-50 text-red-700 border-red-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    pink: "bg-[#fff6f7] text-[#d86a78] border-[#f2c6cc]",
    blue: "bg-[#eef3ff] text-[#4f7cff] border-[#dfe7fb]",
    slate: "bg-slate-50 text-slate-600 border-slate-100",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function statusTone(status) {
  if (status === "ACTIVE") return "green";
  if (status === "INACTIVE" || status === "DELETED") return "red";
  if (String(status || "").includes("SCHEDULED")) return "amber";
  return "slate";
}

function Modal({ open, title, subtitle, children, onClose, width = "max-w-[1080px]" }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/45 px-4 py-6">
      <div
        className={`relative w-full ${width} max-h-[92vh] overflow-hidden rounded-[28px] border bg-white shadow-2xl`}
        style={{ borderColor: BORDER }}
      >
        <div
          className="flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6"
          style={{ borderColor: BORDER }}
        >
          <div>
            <h3 className="text-[24px] font-bold text-slate-900">{title}</h3>
            {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[calc(92vh-84px)] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder = "",
  type = "text",
  required = false,
  icon: Icon = null,
  maxLength,
  min,
  max,
}) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </span>

      <div className="relative">
        {Icon ? (
          <Icon
            size={17}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
        ) : null}

        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          min={min}
          max={max}
          className={`h-[54px] w-full min-w-0 rounded-[16px] border bg-white ${Icon ? "pl-12" : "pl-4"} ${maxLength ? "pr-16" : "pr-4"} text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]`}
          style={{ borderColor: "#dfe5ee" }}
        />

        {maxLength ? (
          <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-medium text-slate-400">
            {String(value || "").length}/{maxLength}
          </div>
        ) : null}
      </div>
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder = "", maxLength = 500 }) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">{label}</span>
      <div className="relative">
        <FileText size={17} className="pointer-events-none absolute left-4 top-4 text-slate-400" />
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={5}
          maxLength={maxLength}
          className="w-full min-w-0 resize-none rounded-[16px] border bg-white pb-8 pl-12 pr-4 pt-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]"
          style={{ borderColor: "#dfe5ee" }}
        />
        <div className="pointer-events-none absolute bottom-3 right-4 text-xs font-medium text-slate-400">
          {String(value || "").length}/{maxLength}
        </div>
      </div>
    </label>
  );
}

function useFloatingPosition(
  open,
  anchorRef,
  preferredWidth = null,
  maxMenuHeight = 280,
  placement = "auto",
  estimatedMenuHeight = null
) {
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const width = preferredWidth || rect.width;
      const viewportPadding = 12;
      const menuGap = 8;
      const minHeight = 80;
      const preferredHeight = Math.min(
        maxMenuHeight,
        Math.max(minHeight, Number(estimatedMenuHeight || maxMenuHeight))
      );

      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        Math.max(viewportPadding, window.innerWidth - width - viewportPadding)
      );

      const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - viewportPadding);
      const spaceAbove = Math.max(0, rect.top - viewportPadding);

      const shouldOpenUp =
        placement === "top" ||
        (placement === "auto" && spaceBelow < preferredHeight + menuGap && spaceAbove > spaceBelow);

      if (shouldOpenUp) {
        const maxHeight = Math.max(minHeight, Math.min(maxMenuHeight, spaceAbove - menuGap));
        const actualHeight = Math.min(preferredHeight, maxHeight);
        const top = Math.max(viewportPadding, rect.top - actualHeight - menuGap);

        setPosition({ top, left, width, maxHeight, placement: "top" });
        return;
      }

      const top = rect.bottom + menuGap;
      const maxHeight = Math.max(minHeight, Math.min(maxMenuHeight, spaceBelow));
      setPosition({ top, left, width, maxHeight, placement: "bottom" });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef, preferredWidth, maxMenuHeight, placement, estimatedMenuHeight]);

  return position;
}

function FloatingSelect({
  value,
  onValueChange,
  options = [],
  placeholder = "Select",
  icon: Icon = null,
  buttonClassName = "",
  menuWidth = null,
  menuPlacement = "auto",
  menuMaxHeight = 300,
}) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const estimatedMenuHeight = Math.min(menuMaxHeight, Math.max(80, (options.length || 1) * 42 + 12));
  const position = useFloatingPosition(
    open,
    buttonRef,
    menuWidth,
    menuMaxHeight,
    menuPlacement,
    estimatedMenuHeight
  );

  const selected = options.find((option) => String(option.value) === String(value));

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const menu = open && position
    ? createPortal(
        <div
          ref={menuRef}
          className="fixed z-[2400] overflow-hidden rounded-[16px] border border-[#f2c6cc] bg-white p-1.5 shadow-[0_18px_50px_rgba(15,23,42,0.16)]"
          style={{
            top: position.top,
            left: position.left,
            width: position.width,
            maxHeight: position.maxHeight,
          }}
        >
          <div className="max-h-full overflow-y-auto pr-1">
            {options.map((option) => {
              const active = String(option.value) === String(value);
              return (
                <button
                  key={option.value || "__all"}
                  type="button"
                  onClick={() => {
                    onValueChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-[12px] px-3 py-2.5 text-left text-sm font-semibold transition ${
                    active ? "bg-[#fff1f3] text-[#c95b69]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {active ? <Check size={15} /> : null}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-[54px] w-full min-w-0 items-center justify-between gap-3 rounded-[16px] border bg-white ${Icon ? "pl-4" : "pl-4"} pr-4 text-left text-sm text-slate-700 outline-none transition hover:bg-[#fffafb] focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3] ${buttonClassName}`}
        style={{ borderColor: open ? BORDER : "#dfe5ee" }}
      >
        <span className="flex min-w-0 items-center gap-3">
          {Icon ? <Icon size={17} className="shrink-0 text-slate-400" /> : null}
          <span className={`${selected ? "text-slate-700" : "text-slate-400"} truncate`}>
            {selected?.label || placeholder}
          </span>
        </span>
        <ChevronDown size={17} className={`shrink-0 text-slate-400 transition ${open ? "rotate-180 text-[#d86a78]" : ""}`} />
      </button>
      {menu}
    </>
  );
}

function SelectInput({ label, value, onChange, options, icon: Icon = null }) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">{label}</span>
      <FloatingSelect
        value={value}
        options={options}
        icon={Icon}
        onValueChange={(nextValue) => onChange({ target: { value: nextValue } })}
      />
    </label>
  );
}

function InlineSelect({
  label = "",
  value,
  onChange,
  options,
  minWidth = 150,
  placeholder = "Select",
  menuPlacement = "auto",
  menuMaxHeight = 300,
}) {
  return (
    <label className="block shrink-0" style={{ minWidth }}>
      {label ? <span className="mb-1 block text-xs font-bold uppercase tracking-[0.04em] text-slate-400">{label}</span> : null}
      <FloatingSelect
        value={value}
        options={options}
        placeholder={placeholder}
        onValueChange={onChange}
        menuPlacement={menuPlacement}
        menuMaxHeight={menuMaxHeight}
        buttonClassName="!h-[40px] !rounded-[12px] !py-0 !text-[13px] !font-semibold focus:!ring-2 focus:!ring-[#f7d4da]"
      />
    </label>
  );
}

function parseDateValue(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function toDateValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateLabel(value) {
  const date = parseDateValue(value);
  if (!date) return "Select date";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function DatePickerInput({ label, value, onChange, required = false }) {
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const selectedDate = parseDateValue(value);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  const position = useFloatingPosition(open, buttonRef, 330, 390);

  useEffect(() => {
    if (open) setViewDate(selectedDate || new Date());
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const firstOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const monthStartDay = firstOfMonth.getDay();
  const gridStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1 - monthStartDay);
  const days = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
  const currentMonth = viewDate.getMonth();
  const todayValue = toDateValue(new Date());
  const selectedValue = value || "";

  const panel = open && position
    ? createPortal(
        <div
          ref={panelRef}
          className="fixed z-[2450] rounded-[20px] border border-[#f2c6cc] bg-white p-4 shadow-[0_20px_55px_rgba(15,23,42,0.18)]"
          style={{ top: position.top, left: position.left, width: position.width }}
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
              className="flex h-9 w-9 items-center justify-center rounded-[12px] border text-slate-500 transition hover:bg-slate-50"
              style={{ borderColor: "#e7eaf0" }}
            >
              <ChevronLeft size={17} />
            </button>
            <div className="text-sm font-bold text-slate-900">
              {viewDate.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
            </div>
            <button
              type="button"
              onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
              className="flex h-9 w-9 items-center justify-center rounded-[12px] border text-slate-500 transition hover:bg-slate-50"
              style={{ borderColor: "#e7eaf0" }}
            >
              <ChevronRight size={17} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
              <div key={`${day}-${index}`} className="py-1.5">{day}</div>
            ))}
          </div>

          <div className="mt-1 grid grid-cols-7 gap-1">
            {days.map((date) => {
              const dateValue = toDateValue(date);
              const active = dateValue === selectedValue;
              const muted = date.getMonth() !== currentMonth;
              const isToday = dateValue === todayValue;
              return (
                <button
                  key={dateValue}
                  type="button"
                  onClick={() => {
                    onChange(dateValue);
                    setOpen(false);
                  }}
                  className={`flex h-9 items-center justify-center rounded-[12px] text-sm font-semibold transition ${
                    active
                      ? "bg-[#d86a78] text-white shadow-[0_10px_22px_rgba(216,106,120,0.22)]"
                      : isToday
                        ? "bg-[#fff1f3] text-[#d86a78]"
                        : muted
                          ? "text-slate-300 hover:bg-slate-50"
                          : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t pt-3" style={{ borderColor: "#eef2f7" }}>
            <button
              type="button"
              onClick={() => {
                onChange(todayValue);
                setOpen(false);
              }}
              className="rounded-[12px] bg-[#fff1f3] px-3 py-2 text-xs font-bold text-[#c95b69] transition hover:bg-[#ffe5ea]"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="rounded-[12px] bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-200"
            >
              Clear
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </span>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-[54px] w-full items-center justify-between gap-3 rounded-[16px] border bg-white px-4 text-left text-sm outline-none transition hover:bg-[#fffafb] focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]"
        style={{ borderColor: open ? BORDER : "#dfe5ee" }}
      >
        <span className="flex min-w-0 items-center gap-3">
          <CalendarDays size={17} className="shrink-0 text-slate-400" />
          <span className={`${value ? "text-slate-700" : "text-slate-400"} truncate`}>{formatDateLabel(value)}</span>
        </span>
        <ChevronDown size={17} className={`shrink-0 text-slate-400 transition ${open ? "rotate-180 text-[#d86a78]" : ""}`} />
      </button>
      {panel}
    </label>
  );
}

function TimeInput({ label, value, onChange, required = false }) {
  return (
    <label className="block space-y-2.5">
      <span className="text-[13px] font-semibold text-slate-800">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </span>
      <div className="relative">
        <Clock3 size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-[54px] w-full rounded-[16px] border bg-white pl-12 pr-4 text-sm font-semibold text-slate-700 outline-none transition [color-scheme:light] focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]"
          style={{ borderColor: "#dfe5ee" }}
        />
      </div>
    </label>
  );
}

function PremiumMultiSelect({
  label,
  value = [],
  onChange,
  options = [],
  placeholder = "Select options",
  helperText = "",
  searchable = true,
}) {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const normalizedValue = useMemo(
    () => (Array.isArray(value) ? value.map(String) : []),
    [value]
  );

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => String(option.label || "").toLowerCase().includes(q));
  }, [options, query]);

  const selectedOptions = useMemo(() => {
    return options.filter((option) => normalizedValue.includes(String(option.value)));
  }, [normalizedValue, options]);

  const toggleValue = (nextValue) => {
    const asString = String(nextValue);
    const exists = normalizedValue.includes(asString);
    onChange(exists ? normalizedValue.filter((item) => item !== asString) : [...normalizedValue, asString]);
  };

  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => normalizedValue.includes(String(option.value)));

  const handleSelectAll = () => {
    const filteredValues = filteredOptions.map((option) => String(option.value));
    if (allFilteredSelected) {
      onChange(normalizedValue.filter((item) => !filteredValues.includes(item)));
      return;
    }
    onChange(Array.from(new Set([...normalizedValue, ...filteredValues])));
  };

  return (
    <div ref={wrapperRef} className="relative space-y-2.5">
      <span className="block text-[13px] font-semibold text-slate-800">{label}</span>

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex min-h-[54px] w-full items-center justify-between gap-3 rounded-[16px] border bg-white px-4 py-2 text-left transition hover:bg-[#fffafb] focus:outline-none focus:ring-4 focus:ring-[#fdf1f3]"
        style={{ borderColor: open ? BORDER : "#dfe5ee" }}
      >
        <div className="min-w-0 flex-1">
          {selectedOptions.length ? (
            <div className="flex flex-wrap gap-2">
              {selectedOptions.slice(0, 3).map((item) => (
                <span
                  key={item.value}
                  className="inline-flex max-w-[140px] items-center rounded-full bg-[#fff1f3] px-3 py-1 text-xs font-semibold text-[#c95b69]"
                >
                  <span className="truncate">{item.label}</span>
                </span>
              ))}

              {selectedOptions.length > 3 ? (
                <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  +{selectedOptions.length - 3} more
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-sm text-slate-400">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-2 pl-2">
          {selectedOptions.length ? (
            <span className="rounded-full bg-[#fff4f6] px-2.5 py-1 text-xs font-semibold text-[#d86a78]">
              {selectedOptions.length}
            </span>
          ) : null}
          <ChevronDown size={16} className={`text-slate-500 transition ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open ? (
        <div
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 rounded-[20px] border bg-white p-3 shadow-[0_12px_40px_rgba(15,23,42,0.08)]"
          style={{ borderColor: BORDER }}
        >
          {searchable ? (
            <div className="relative mb-3">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}`}
                className="w-full rounded-[14px] border bg-white py-2.5 pl-9 pr-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[#f7d4da]"
                style={{ borderColor: BORDER }}
              />
            </div>
          ) : null}

          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="rounded-[12px] bg-[#fff1f3] px-3 py-2 text-xs font-semibold text-[#c95b69] transition hover:bg-[#ffe5ea]"
            >
              {allFilteredSelected ? "Unselect All" : "Select All"}
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              className="rounded-[12px] bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
            >
              Clear All
            </button>
          </div>

          <div className="max-h-[260px] space-y-2 overflow-y-auto pr-1">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const checked = normalizedValue.includes(String(option.value));

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleValue(option.value)}
                    className={`flex w-full items-center justify-between gap-3 rounded-[14px] border px-3 py-2.5 text-left transition ${checked ? "border-[#f2b9c2] bg-[#fff4f6]" : "border-transparent bg-slate-50 hover:bg-[#fffafb]"}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-700">{option.label}</div>
                    </div>

                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-md border transition ${checked ? "border-[#d86a78] bg-[#d86a78] text-white" : "border-slate-300 bg-white text-transparent"}`}
                    >
                      <Check size={13} />
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="rounded-[14px] bg-slate-50 px-3 py-6 text-center text-sm text-slate-400">
                No options found
              </div>
            )}
          </div>
        </div>
      ) : null}

      {helperText ? <p className="text-xs text-slate-500">{helperText}</p> : null}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <div className="space-y-2.5">
      <div className="text-[13px] font-semibold text-slate-800">{label}</div>
      <div className="flex h-[54px] items-center justify-end">
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`relative h-8 w-[62px] rounded-full transition ${checked ? "bg-[#c1275b]" : "bg-slate-300"}`}
        >
          <span
            className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm transition ${checked ? "left-[32px]" : "left-1"}`}
          />
        </button>
      </div>
    </div>
  );
}

function normalizeDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function normalizeTime(value) {
  if (!value) return "";
  return String(value).slice(0, 5);
}

function formatHumanDate(value) {
  const normalized = normalizeDate(value);
  if (!normalized) return "-";

  const [year, month, day] = normalized.split("-").map(Number);
  if (!year || !month || !day) return normalized;

  return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatHumanTime(value) {
  if (!value) return "--:--";
  const text = String(value);
  if (/^\d{2}:\d{2}/.test(text)) return text.slice(0, 5);
  if (text.includes("T")) return text.slice(11, 16);
  return normalizeTime(text) || "--:--";
}

function formatDayNames(days = []) {
  const normalizedDays = Array.isArray(days) ? days.map(String) : [];
  if (!normalizedDays.length || normalizedDays.length === 7) return "Every day";

  return WEEK_DAYS
    .filter((day) => normalizedDays.includes(day.value))
    .map((day) => day.label)
    .join(", ");
}

function getConflictDisplay(item = {}) {
  const startDate = item.startDate || item.start_date || item.startsAt || item.starts_at;
  const endDate = item.endDate || item.end_date || item.endsAt || item.ends_at;
  const startTime = item.dailyStartTime || item.daily_start_time || startDate;
  const endTime = item.dailyEndTime || item.daily_end_time || endDate;

  const fromDate = formatHumanDate(startDate);
  const toDate = formatHumanDate(endDate);
  const dateText = fromDate === toDate ? fromDate : `${fromDate} - ${toDate}`;

  return {
    title: item.title || item.short_video_title || "Untitled video",
    order: item.displayOrder ?? item.display_order ?? 100,
    dateText,
    timeText: `${formatHumanTime(startTime)} - ${formatHumanTime(endTime)}`,
    daysText: formatDayNames(item.daysOfWeek || item.days_of_week || []),
  };
}

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function isUuidLike(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || "").trim());
}

function responseItems(res) {
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

function resolveShortVideoProductContext(contexts = []) {
  const normalized = (Array.isArray(contexts) ? contexts : [])
    .map((item) => String(item || "").trim().toUpperCase())
    .filter(Boolean);

  if (normalized.length === 1 && normalized[0] === "FOOD") return "FOOD";
  return "GROCERY";
}

function formatShortVideoPrice(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function productOptionKey(option) {
  return String(option?.productId || option?.value || option?.id || "");
}

function variantOptionKey(option) {
  return String(option?.variantId || option?.value || option?.id || "");
}


function VideoPreviewCard({ form, videoUrl, thumbUrl, editing }) {
  const contextText = form.filters?.contexts?.length ? form.filters.contexts.join(" + ") : "GROCERY";
  const statusText = form.isActive ? "Active" : "Inactive";

  return (
    <Card className="p-4">
      <div className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-900">
        <Eye size={16} />
        Quick Preview
      </div>

      <div className="overflow-hidden rounded-[20px] border bg-slate-50" style={{ borderColor: "#e8edf4" }}>
        <div className="flex aspect-[9/12] max-h-[360px] items-center justify-center overflow-hidden bg-slate-100">
          {videoUrl ? (
            <video src={videoUrl} className="h-full w-full object-cover" muted controls />
          ) : thumbUrl ? (
            <img src={thumbUrl} alt="thumbnail preview" className="h-full w-full object-cover" />
          ) : editing ? (
            <div className="flex flex-col items-center text-center text-slate-400">
              <PlaySquare size={42} />
              <div className="mt-3 text-sm font-semibold">Current saved video</div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-center text-slate-400">
              <div className="rounded-[20px] bg-[#fff1f3] p-5 text-[#d86a78]">
                <PlaySquare size={42} />
              </div>
              <div className="mt-4 text-sm font-semibold">Video preview</div>
            </div>
          )}
        </div>

        <div className="bg-white p-4 text-center">
          <div className="line-clamp-1 text-[15px] font-bold text-slate-900">
            {form.title || "Video title"}
          </div>
          <div className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
            {form.description || "Video description will appear here"}
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Pill tone="slate">{form.targetType === "FILTERED" ? "Filtered" : "All Users"}</Pill>
            <Pill tone="pink">{contextText}</Pill>
            <Pill tone={form.isActive ? "green" : "red"}>{statusText}</Pill>
          </div>
        </div>
      </div>
    </Card>
  );
}

function FileUploadBox({ label, helper, accept, onChange, required, previewUrl, type = "video" }) {
  const inputRef = useRef(null);
  const isVideo = type === "video";
  const hasPreview = Boolean(previewUrl);
  const chooseLabel = isVideo ? "video file" : "thumbnail";

  const handleFileChange = (e) => {
    onChange(e.target.files?.[0] || null);
  };

  const openPicker = () => {
    inputRef.current?.click();
  };

  return (
    <Card className="p-4">
      <div className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-900">
        <UploadCloud size={17} />
        {label} {required ? <span className="text-red-500">*</span> : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="sr-only"
      />

      {hasPreview ? (
        <div
          className={`group relative min-h-[172px] overflow-hidden rounded-[20px] border border-dashed bg-[#fffafb] ${
            isVideo ? "p-3" : "p-0"
          }`}
          style={{ borderColor: BORDER }}
        >
          {isVideo ? (
            <div className="relative h-[172px] w-full overflow-hidden rounded-[18px] bg-black">
              <video
                controls
                playsInline
                muted
                preload="metadata"
                className="h-full w-full object-contain"
                src={previewUrl}
              />
            </div>
          ) : (
            <div className="relative aspect-video min-h-[172px] w-full overflow-hidden rounded-[18px] bg-slate-100">
              <img alt="thumbnail" className="h-full w-full object-cover" src={previewUrl} />
            </div>
          )}

          <button
            type="button"
            onClick={openPicker}
            className="absolute right-3 top-3 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold text-slate-900 shadow-sm ring-1 ring-slate-200 transition hover:bg-[#fff4f6] hover:text-[#d86a78]"
          >
            Change {isVideo ? "video" : "thumbnail"}
          </button>

          {helper ? (
            <div className="mt-3 rounded-[14px] bg-white px-3 py-2 text-xs font-semibold leading-5 text-slate-500 ring-1 ring-slate-100">
              {helper}
            </div>
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="flex min-h-[172px] w-full cursor-pointer flex-col items-center justify-center rounded-[20px] border border-dashed bg-[#fffafb] px-4 py-6 text-center transition hover:bg-[#fff4f6]"
          style={{ borderColor: BORDER }}
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
            {isVideo ? <PlaySquare size={26} /> : <UploadCloud size={26} />}
          </div>
          <div className="mt-4 text-sm font-bold text-slate-900">Choose {chooseLabel}</div>
          <div className="mt-1 text-xs leading-5 text-slate-500">{helper}</div>
        </button>
      )}
    </Card>
  );
}

function ProductSearchSelect({ productId, productVariantId, contexts = [], warehouseIds = [], onChange }) {
  const wrapperRef = useRef(null);
  const productSeq = useRef(0);
  const hydrateSeq = useRef(0);
  const variantSeq = useRef(0);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);

  const searchContext = useMemo(() => resolveShortVideoProductContext(contexts), [contexts]);
  const cleanWarehouseIds = useMemo(
    () => (Array.isArray(warehouseIds) ? warehouseIds.map(toOptionValue).filter(Boolean) : []),
    [warehouseIds]
  );
  const warehouseKey = cleanWarehouseIds.join(",");
  const cleanProductId = String(productId || "").trim();
  const cleanVariantId = String(productVariantId || "").trim();
  const selectedProductType = selectedProduct?.type || (searchContext === "FOOD" ? "FOOD_ITEM" : "GROCERY_PRODUCT");

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    setOptions([]);
    setLookupError("");
  }, [searchContext, warehouseKey]);

  useEffect(() => {
    const term = query.trim();
    const canSearch = term.length >= 2 || isUuidLike(term);

    if (!canSearch) {
      setOptions([]);
      setLoading(false);
      setLookupError("");
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      const seq = productSeq.current + 1;
      productSeq.current = seq;
      setLoading(true);
      setLookupError("");

      try {
        const res = await shortVideosService.searchProductOptions({
          q: term,
          warehouseIds: cleanWarehouseIds,
          context: searchContext,
          limit: 20,
        });
        if (productSeq.current !== seq) return;
        setOptions(responseItems(res));
      } catch (err) {
        if (productSeq.current !== seq) return;
        setOptions([]);
        setLookupError(readApiError(err, "Unable to search products"));
      } finally {
        if (productSeq.current === seq) setLoading(false);
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [query, searchContext, warehouseKey]);

  useEffect(() => {
    if (!cleanProductId || !isUuidLike(cleanProductId)) {
      setSelectedProduct(null);
      setVariants([]);
      return undefined;
    }

    if (String(selectedProduct?.productId || selectedProduct?.value || "") === cleanProductId) return undefined;

    const seq = hydrateSeq.current + 1;
    hydrateSeq.current = seq;

    (async () => {
      try {
        const res = await shortVideosService.searchProductOptions({
          q: cleanProductId,
          warehouseIds: cleanWarehouseIds,
          context: searchContext,
          limit: 1,
        });
        if (hydrateSeq.current !== seq) return;
        const found = responseItems(res)[0] || null;
        if (found) {
          setSelectedProduct(found);
          setQuery((prev) => prev || found.label || cleanProductId);
        }
      } catch {
        // Keep the saved product id visible even if hydration fails.
      }
    })();

    return undefined;
  }, [cleanProductId, searchContext, warehouseKey, selectedProduct]);

  useEffect(() => {
    if (!cleanProductId || !isUuidLike(cleanProductId)) {
      setVariants([]);
      setVariantsLoading(false);
      return undefined;
    }

    const seq = variantSeq.current + 1;
    variantSeq.current = seq;
    setVariantsLoading(true);

    (async () => {
      try {
        const res = await shortVideosService.getProductVariants(cleanProductId, {
          warehouseIds: cleanWarehouseIds,
          productType: selectedProductType,
          limit: 50,
        });
        if (variantSeq.current !== seq) return;
        setVariants(responseItems(res));
      } catch {
        if (variantSeq.current !== seq) return;
        setVariants([]);
      } finally {
        if (variantSeq.current === seq) setVariantsLoading(false);
      }
    })();

    return undefined;
  }, [cleanProductId, selectedProductType, warehouseKey]);

  const selectProduct = (option) => {
    const nextProductId = productOptionKey(option);
    const nextVariantId = String(option?.productVariantId || option?.defaultVariantId || "");

    setSelectedProduct(option);
    setQuery(option?.label || nextProductId);
    setOptions([]);
    setOpen(false);

    onChange({
      productId: nextProductId,
      productVariantId: nextVariantId,
    });
  };

  const clearProduct = () => {
    setSelectedProduct(null);
    setQuery("");
    setOptions([]);
    setVariants([]);
    onChange({ productId: "", productVariantId: "" });
  };

  const selectedVariant = variants.find((item) => variantOptionKey(item) === cleanVariantId) || null;

  return (
    <div className="space-y-3 rounded-[20px] border bg-[#fffafb] p-4" style={{ borderColor: BORDER }}>
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-[13px] font-semibold text-slate-800">
            Product <span className="text-red-500">*</span>
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Search starts after 2 characters and returns max 20 records.
          </p>
        </div>
        <Pill tone={searchContext === "FOOD" ? "amber" : "pink"}>
          {searchContext === "FOOD" ? "Food items" : "Grocery products"}
        </Pill>
      </div>

      <div ref={wrapperRef} className="relative">
        <Search size={17} className="pointer-events-none absolute left-4 top-[19px] text-slate-400" />
        <input
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          placeholder={searchContext === "FOOD" ? "Search food item by name" : "Search product by name, SKU, barcode, variant"}
          className="h-[54px] w-full rounded-[16px] border bg-white pl-12 pr-12 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]"
          style={{ borderColor: open ? BORDER : "#dfe5ee" }}
        />
        {query || cleanProductId ? (
          <button
            type="button"
            onClick={clearProduct}
            className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-[#d86a78]"
          >
            <X size={16} />
          </button>
        ) : null}

        {open ? (
          <div
            className="absolute left-0 right-0 top-[calc(100%+8px)] z-[80] rounded-[20px] border bg-white p-2 shadow-[0_18px_48px_rgba(15,23,42,0.14)]"
            style={{ borderColor: BORDER }}
          >
            {query.trim().length < 2 && !isUuidLike(query) ? (
              <div className="rounded-[16px] bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
                Type at least 2 characters to search. No open-ended product loading.
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center gap-2 rounded-[16px] bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-500">
                <Loader2 size={16} className="animate-spin" /> Searching products...
              </div>
            ) : lookupError ? (
              <div className="rounded-[16px] bg-red-50 px-4 py-4 text-sm font-semibold text-red-600">{lookupError}</div>
            ) : options.length ? (
              <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                {options.map((option) => {
                  const optionProductId = productOptionKey(option);
                  const active = optionProductId === cleanProductId;
                  const imageUrl = buildShortVideoAssetUrl(option.imageUrl);
                  const price = formatShortVideoPrice(option.sellingPrice);
                  const mrp = formatShortVideoPrice(option.mrp);

                  return (
                    <button
                      key={`${optionProductId}-${option.type || "product"}`}
                      type="button"
                      onClick={() => selectProduct(option)}
                      className={`flex w-full items-center gap-3 rounded-[16px] border p-3 text-left transition ${active ? "border-[#f2b9c2] bg-[#fff4f6]" : "border-transparent bg-slate-50 hover:bg-[#fffafb]"}`}
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[14px] bg-white ring-1 ring-slate-100">
                        {imageUrl ? <img src={imageUrl} alt="" className="h-full w-full object-cover" /> : <Film size={18} className="text-slate-300" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-slate-900">{option.label || optionProductId}</div>
                        {option.subtitle ? <div className="mt-0.5 truncate text-xs text-slate-500">{option.subtitle}</div> : null}
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-400">
                          {option.defaultVariantLabel ? <span>{option.defaultVariantLabel}</span> : null}
                          {Number(option.variantCount || 0) > 1 ? <span>{option.variantCount} variants</span> : null}
                          {price ? <span className="text-emerald-600">{price}</span> : null}
                          {mrp && mrp !== price ? <span className="line-through">{mrp}</span> : null}
                        </div>
                      </div>
                      {active ? <CheckCircle2 size={18} className="shrink-0 text-[#d86a78]" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-[16px] bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
                No matching product found.
              </div>
            )}
          </div>
        ) : null}
      </div>

      {cleanProductId ? (
        <div className="rounded-[16px] border bg-white p-3" style={{ borderColor: "#e8edf4" }}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="text-sm font-bold text-slate-900">
                {selectedProduct?.label || "Selected product"}
              </div>
              {selectedProduct?.subtitle ? <div className="mt-1 text-xs text-slate-500">{selectedProduct.subtitle}</div> : null}
              <div className="mt-2 break-all rounded-[12px] bg-slate-50 px-3 py-2 text-[11px] font-semibold text-slate-500">
                Product ID: <span className="text-slate-700">{cleanProductId}</span>
              </div>
            </div>

            <div className="w-full lg:w-[320px]">
              <label className="block space-y-2">
                <span className="text-xs font-bold text-slate-700">Variant ID optional</span>
                <div className="relative">
                  {variantsLoading ? (
                    <Loader2 size={15} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
                  ) : null}
                  <select
                    value={cleanVariantId}
                    onChange={(event) => onChange({ productId: cleanProductId, productVariantId: event.target.value })}
                    className="h-[46px] w-full rounded-[14px] border bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-[#efc5cd] focus:ring-4 focus:ring-[#fdf1f3]"
                    style={{ borderColor: "#dfe5ee" }}
                  >
                    <option value="">Auto/default variant</option>
                    {variants.map((variant) => (
                      <option key={variantOptionKey(variant)} value={variantOptionKey(variant)}>
                        {variant.label || variantOptionKey(variant)}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              {cleanVariantId ? (
                <div className="mt-2 break-all rounded-[12px] bg-slate-50 px-3 py-2 text-[11px] font-semibold text-slate-500">
                  Variant ID: <span className="text-slate-700">{cleanVariantId}</span>
                  {selectedVariant?.label ? <span className="ml-1 text-slate-400">({selectedVariant.label})</span> : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function VideoForm({ form, setForm, filterOptions, editing, saving, conflicts, error, onDismissError, onCheckConflicts, onSubmit, onClose }) {
  const localVideoPreviewUrl = useMemo(
    () => (form.video ? URL.createObjectURL(form.video) : ""),
    [form.video]
  );
  const localThumbPreviewUrl = useMemo(
    () => (form.thumbnail ? URL.createObjectURL(form.thumbnail) : ""),
    [form.thumbnail]
  );

  const previewVideoUrl = localVideoPreviewUrl || form.existingVideoUrl || "";
  const previewThumbUrl = localThumbPreviewUrl || form.existingThumbnailUrl || "";

  useEffect(() => {
    return () => {
      if (localVideoPreviewUrl) URL.revokeObjectURL(localVideoPreviewUrl);
      if (localThumbPreviewUrl) URL.revokeObjectURL(localThumbPreviewUrl);
    };
  }, [localVideoPreviewUrl, localThumbPreviewUrl]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setFilter = (key, value) => setForm((prev) => ({ ...prev, filters: { ...prev.filters, [key]: value } }));

  return (
    <form onSubmit={onSubmit}>
      <div className="grid min-h-[620px] lg:grid-cols-[1fr_340px]">
        <div className="space-y-5 p-5 sm:p-6">
          <ErrorNotice message={error} onClose={onDismissError} />

          {conflicts.length ? (
            <div className="rounded-[18px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <div className="flex items-center gap-2 font-bold">
                <AlertTriangle size={17} /> Same time videos already exist
              </div>
              <div className="mt-2 text-xs leading-5">
                Upload is still allowed. Lower display order plays first in the slider, so use 1/10/50 for higher priority and keep 100 for normal priority.
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {conflicts.slice(0, 4).map((item, index) => {
                  const conflict = getConflictDisplay(item);

                  return (
                    <div
                      key={item.id || `${conflict.title}-${index}`}
                      className="rounded-xl border border-amber-100 bg-white px-3 py-3 text-xs text-slate-700 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate font-bold text-slate-900">{conflict.title}</div>
                          <div className="mt-1 flex items-center gap-1.5 text-slate-500">
                            <CalendarDays size={13} />
                            <span>{conflict.dateText}</span>
                          </div>
                          <div className="mt-1 flex items-center gap-1.5 text-slate-500">
                            <Clock3 size={13} />
                            <span>{conflict.timeText}</span>
                          </div>
                          <div className="mt-1 text-slate-400">Days: {conflict.daysText}</div>
                        </div>

                        <span className="shrink-0 rounded-full bg-[#fff1f3] px-2.5 py-1 text-[11px] font-bold text-[#c95b69]">
                          Order {conflict.order}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <TextInput
              label="Video title"
              required
              icon={Type}
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Enter video title"
              maxLength={120}
            />
            <div className="space-y-2">
              <TextInput
                label="Display order"
                type="number"
                min="0"
                max="9999"
                icon={SlidersHorizontal}
                value={form.displayOrder}
                onChange={(e) => set("displayOrder", e.target.value)}
                placeholder="100"
              />
              <p className="px-1 text-xs leading-5 text-slate-500">
                Default is 100. Lower number plays first: 1 before 10, 10 before 50, 50 before 100.
              </p>
            </div>
          </div>

          <TextArea
            label="Description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Video description"
            maxLength={500}
          />

          <div className="grid gap-4 md:grid-cols-3">
            <SelectInput
              label="Target Type"
              icon={Users}
              value={form.targetType}
              onChange={(e) => set("targetType", e.target.value)}
              options={[
                { label: "All Users", value: "ALL" },
                { label: "Selected warehouses", value: "FILTERED" },
              ]}
            />
            <SelectInput
              label="Redirect type"
              icon={Link2}
              value={form.redirectType}
              onChange={(e) => set("redirectType", e.target.value)}
              options={[
                { label: "Product", value: "PRODUCT" },
                { label: "Category", value: "CATEGORY" },
                { label: "Deep link", value: "DEEPLINK" },
                { label: "No redirect", value: "NONE" },
              ]}
            />
            <Toggle label="Video Active" checked={form.isActive} onChange={(next) => set("isActive", next)} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <PremiumMultiSelect
              label="Business Section"
              value={form.filters.contexts}
              onChange={(value) => setFilter("contexts", value)}
              options={CONTEXT_OPTIONS}
              searchable={false}
            />
            <TextInput
              label="CTA label"
              icon={Type}
              value={form.ctaLabel}
              onChange={(e) => set("ctaLabel", e.target.value)}
              placeholder="Shop now"
              maxLength={50}
            />
          </div>

          {form.targetType === "FILTERED" ? (
            <div className="max-w-[680px]">
              <PremiumMultiSelect
                label="Warehouses"
                value={form.filters.warehouseIds}
                onChange={(value) => setFilter("warehouseIds", value)}
                options={filterOptions.warehouses || []}
                placeholder="Select at least one warehouse"
                helperText="Only selected warehouses will receive this video."
              />
            </div>
          ) : null}

          {form.redirectType === "PRODUCT" ? (
            <ProductSearchSelect
              productId={form.productId}
              productVariantId={form.productVariantId}
              contexts={form.filters.contexts}
              warehouseIds={form.targetType === "FILTERED" ? form.filters.warehouseIds : []}
              onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            />
          ) : null}

          {form.redirectType === "CATEGORY" ? (
            <TextInput
              label="Category ID"
              required
              icon={Link2}
              value={form.productId}
              onChange={(e) => set("productId", e.target.value)}
              placeholder="Category ID"
            />
          ) : null}

          {form.redirectType === "DEEPLINK" ? (
            <TextInput
              label="Deep link"
              required
              icon={Link2}
              value={form.deepLink}
              onChange={(e) => set("deepLink", e.target.value)}
              placeholder="wholesum://product/123"
            />
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <DatePickerInput
              label="Start date"
              required
              value={form.startDate}
              onChange={(nextValue) => set("startDate", nextValue)}
            />
            <DatePickerInput
              label="End date"
              required
              value={form.endDate}
              onChange={(nextValue) => set("endDate", nextValue)}
            />
          </div>

          <div className="space-y-2">
            <div className="grid gap-4 md:grid-cols-2">
              <TimeInput
                label="Daily start time"
                required
                value={form.dailyStartTime}
                onChange={(nextValue) => set("dailyStartTime", nextValue)}
              />
              <TimeInput
                label="Daily end time"
                required
                value={form.dailyEndTime}
                onChange={(nextValue) => set("dailyEndTime", nextValue)}
              />
            </div>
            {form.dailyStartTime &&
            form.dailyEndTime &&
            form.dailyEndTime <= form.dailyStartTime ? (
              <p className="px-1 text-xs font-semibold leading-5 text-amber-700">
                Overnight schedule: the video starts on the selected day and remains visible until the end time next morning.
              </p>
            ) : null}
          </div>

          <div className="space-y-2.5">
            <div className="text-[13px] font-semibold text-slate-800">Show on days</div>
            <div className="flex flex-wrap gap-2">
              {WEEK_DAYS.map((day) => {
                const checked = form.daysOfWeek.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => set("daysOfWeek", checked ? form.daysOfWeek.filter((x) => x !== day.value) : [...form.daysOfWeek, day.value])}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition ${checked ? "bg-[#fff6f7] text-[#d86a78]" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                    style={{ borderColor: checked ? BORDER : "#dfe5ee" }}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-l bg-[#fcfcfd] p-5 sm:p-6" style={{ borderColor: "#eef2f7" }}>
          <div className="sticky top-0 space-y-5">
            <FileUploadBox
              label="Video file"
              required={!editing}
              accept="video/mp4,video/webm,video/quicktime"
              onChange={(file) => set("video", file)}
              previewUrl={previewVideoUrl}
              type="video"
              helper={editing ? "Current video is kept. Choose a new file only if you want to replace it." : "MP4, WEBM, MOV. Upload is compressed and converted to HLS for app playback."}
            />

            <FileUploadBox
              label="Thumbnail"
              accept="image/jpeg,image/png,image/webp"
              onChange={(file) => set("thumbnail", file)}
              previewUrl={previewThumbUrl}
              type="image"
              helper={editing ? "Current thumbnail is kept. Choose a new image only if you want to replace it." : "JPG, PNG, WEBP"}
            />

            <VideoPreviewCard form={form} videoUrl={previewVideoUrl} thumbUrl={previewThumbUrl} editing={editing} />
          </div>
        </div>
      </div>

      <div
        className="sticky bottom-0 flex flex-wrap justify-end gap-3 border-t bg-white px-5 py-4 sm:px-6"
        style={{ borderColor: BORDER }}
      >
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-[42px] items-center justify-center rounded-[14px] border bg-white px-5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          style={{ borderColor: "#e7eaf0" }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onCheckConflicts}
          className="inline-flex h-[42px] items-center justify-center rounded-[14px] border bg-white px-5 text-sm font-semibold text-amber-700 transition hover:bg-amber-50"
          style={{ borderColor: "#f5d48a" }}
        >
          Check time conflict
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex h-[42px] items-center justify-center gap-2 rounded-[14px] px-5 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(216,106,120,0.20)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
          style={{ backgroundColor: PINK }}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
          {editing ? "Update video" : "Create video"}
        </button>
      </div>
    </form>
  );
}

function buildVideoFormPayload(form) {
  const warehouseIds =
    form.targetType === "FILTERED"
      ? (Array.isArray(form.filters?.warehouseIds) ? form.filters.warehouseIds : [])
          .map(toOptionValue)
          .filter(Boolean)
      : [];

  return {
    ...form,
    filters: {
      contexts:
        Array.isArray(form.filters?.contexts) && form.filters.contexts.length
          ? form.filters.contexts
          : ["GROCERY"],
      warehouseIds: Array.from(new Set(warehouseIds)),
    },
  };
}

function itemToForm(item) {
  return {
    ...emptyForm,
    title: item.title || "",
    description: item.description || "",
    redirectType: item.redirectType || "PRODUCT",
    productId: item.productId || "",
    productVariantId: item.productVariantId || "",
    deepLink: item.deepLink || "",
    ctaLabel: item.ctaLabel || "Shop now",
    targetType: item.targetType || "ALL",
    filters: {
      contexts: item.filters?.contexts?.length ? item.filters.contexts : ["GROCERY"],
      warehouseIds: item.filters?.warehouseIds || [],
    },
    displayOrder: item.displayOrder ?? 100,
    isActive: Boolean(item.isActive),
    startDate: normalizeDate(item.startDate),
    endDate: normalizeDate(item.endDate),
    dailyStartTime: normalizeTime(item.dailyStartTime),
    dailyEndTime: normalizeTime(item.dailyEndTime),
    daysOfWeek: Array.isArray(item.daysOfWeek) && item.daysOfWeek.length ? item.daysOfWeek.map(String) : emptyForm.daysOfWeek,
    video: null,
    thumbnail: null,
    existingVideoUrl: getVideoPreviewUrl(item),
    existingThumbnailUrl: getThumbnailPreviewUrl(item),
  };
}

function StatCard({ icon: Icon, label, value, helper, borderColor, iconClassName, surfaceClassName, waveClassName }) {
  return (
    <div
      className={`group relative min-h-[126px] overflow-hidden rounded-[18px] border p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_16px_38px_rgba(15,23,42,0.08)] ${surfaceClassName || "bg-white"}`}
      style={{ borderColor }}
    >
      <svg aria-hidden="true" viewBox="0 0 220 90" preserveAspectRatio="none" className={`pointer-events-none absolute -bottom-1 right-0 h-[72px] w-[58%] opacity-70 ${waveClassName || "text-slate-100"}`}>
        <path d="M0 82 C25 80 31 52 55 56 C78 61 83 28 108 38 C132 47 142 5 170 18 C192 28 203 8 220 0 L220 90 L0 90 Z" fill="currentColor" />
      </svg>
      <div className="relative z-10 flex items-center gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-[12px] shadow-sm ${iconClassName}`}>
          <Icon size={20} />
        </div>
        <div className="text-[12px] font-bold uppercase tracking-[0.06em] text-slate-500">
          {label}
        </div>
      </div>
      <div className="relative z-10 mt-3 text-[26px] font-black leading-none text-[#101b35]">{value}</div>
      <div className="relative z-10 mt-1.5 text-[11px] font-medium text-slate-500">{helper}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto flex max-w-[360px] flex-col items-center">
      <div className="rounded-full bg-[#fff4f6] p-4 text-[#d86a78]">
        <Film size={28} />
      </div>
      <div className="mt-4 text-lg font-bold text-slate-800">No short videos found</div>
      <div className="mt-1 text-sm text-slate-500">Create your first short video to start using this module.</div>
    </div>
  );
}

function readApiError(err, fallback = "Something went wrong") {
  const data = err?.data || {};
  const collected = [];

  const pushMessage = (value, field = "") => {
    if (!value) return;
    const text = String(value).trim();
    if (!text) return;
    collected.push(field ? `${field}: ${text}` : text);
  };

  const readOne = (item) => {
    if (!item) return;
    if (typeof item === "string") {
      pushMessage(item);
      return;
    }
    if (typeof item !== "object") return;

    const field = item.field || item.path || item.param || item.property || item.key || "";
    pushMessage(item.message || item.msg || item.error || item.reason, field);
  };

  const readMany = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(readOne);
      return;
    }
    if (typeof value === "object") {
      Object.entries(value).forEach(([field, message]) => {
        if (Array.isArray(message)) {
          message.forEach((entry) => readOne(typeof entry === "object" ? entry : { field, message: entry }));
        } else if (typeof message === "object") {
          readOne({ field, ...message });
        } else {
          pushMessage(message, field);
        }
      });
    }
  };

  readMany(data.errors);
  readMany(data.validationErrors);
  readMany(data.details);

  const detailed = [...new Set(collected)].slice(0, 4).join(" • ");
  if (detailed) return detailed;

  const rawMessage = data.message || data.error || err?.message || fallback;
  if (/^validation error$/i.test(String(rawMessage || "").trim())) {
    return "Validation failed. Please check the required fields, targeting, schedule, and redirect details.";
  }

  return rawMessage || fallback;
}

function ErrorNotice({ message, onClose }) {
  if (!message) return null;

  return (
    <div className="rounded-[18px] border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <AlertTriangle size={17} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-bold">Action needed</div>
            <div className="mt-0.5 font-semibold leading-5">{message}</div>
          </div>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-red-500 transition hover:bg-red-100"
            aria-label="Dismiss error"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function getVideoPreviewUrl(item) {
  const raw =
    item?.videoUrl ||
    item?.previewUrl ||
    item?.playbackUrl ||
    item?.streamUrl ||
    item?.hlsUrl ||
    item?.fileUrl ||
    item?.assetUrl ||
    item?.sourceUrl ||
    item?.originalUrl ||
    item?.fullVideoUrl ||
    item?.videoPath ||
    item?.filePath ||
    item?.video_url ||
    item?.preview_url ||
    item?.playback_url ||
    item?.stream_url ||
    item?.file_url ||
    "";
  return buildShortVideoAssetUrl(raw);
}

function getThumbnailPreviewUrl(item) {
  return buildShortVideoAssetUrl(item?.thumbnailUrl || item?.thumbnailPath || item?.thumbnail_url || "");
}

function VideoPreviewModal({ item, onClose }) {
  const videoUrl = getVideoPreviewUrl(item);
  const thumbUrl = getThumbnailPreviewUrl(item);

  return (
    <Modal
      open={Boolean(item)}
      title="Short video preview"
      subtitle="Preview how this video will look before users see it in the app."
      width="max-w-[560px]"
      onClose={onClose}
    >
      <div className="space-y-5 p-5 sm:p-6">
        <div className="overflow-hidden rounded-[26px] border bg-slate-950 shadow-[0_18px_55px_rgba(15,23,42,0.18)]" style={{ borderColor: "#e8edf4" }}>
          <div className="mx-auto flex aspect-[9/16] max-h-[620px] max-w-[360px] items-center justify-center bg-slate-950">
            {videoUrl ? (
              <video
                key={videoUrl}
                src={videoUrl}
                poster={thumbUrl || undefined}
                className="h-full w-full object-contain"
                controls
                autoPlay
                muted
                playsInline
              />
            ) : thumbUrl ? (
              <div className="relative h-full w-full">
                <img src={thumbUrl} alt={item?.title || "Video thumbnail"} className="h-full w-full object-cover opacity-80" />
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 px-6 text-center text-white">
                  <PlaySquare size={46} />
                  <div className="mt-4 text-sm font-bold">Preview URL missing</div>
                  <div className="mt-1 text-xs leading-5 text-white/75">
                    API returned thumbnail, but not a playable video URL.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center px-6 text-center text-white/75">
                <PlaySquare size={54} />
                <div className="mt-4 text-sm font-bold text-white">No preview available</div>
                <div className="mt-1 text-xs leading-5">
                  Saved video URL was not returned in this list response.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-[20px] border bg-white p-4" style={{ borderColor: "#eef2f7" }}>
          <div className="text-base font-bold text-slate-900">{item?.title || "Untitled video"}</div>
          <div className="mt-1 text-sm leading-6 text-slate-500">{item?.description || "No description"}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Pill tone="pink">Order {item?.displayOrder ?? 100}</Pill>
            <Pill tone="slate">{item?.redirectType || "NONE"}</Pill>
            <Pill tone={statusTone(item?.computedStatus)}>{item?.computedStatus || (item?.isActive ? "ACTIVE" : "INACTIVE")}</Pill>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function DeleteConfirmModal({ item, deleting, error, onDismissError, onClose, onConfirm }) {
  const title = item?.title || "this short video";

  return (
    <Modal
      open={Boolean(item)}
      title="Delete short video?"
      subtitle="This is a custom confirmation popup. No default browser alert is used."
      width="max-w-[500px]"
      onClose={deleting ? undefined : onClose}
    >
      <div className="space-y-5 p-5 sm:p-6">
        <ErrorNotice message={error} onClose={onDismissError} />

        <div className="rounded-[24px] border bg-[#fffafb] p-5 text-center" style={{ borderColor: BORDER }}>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
            <Trash2 size={30} />
          </div>

          <div className="mt-4 text-lg font-bold text-slate-900">Delete “{title}”?</div>
          <div className="mx-auto mt-2 max-w-[360px] text-sm leading-6 text-slate-500">
            This video will be removed from the active list, but it will stay available in history.
          </div>

          <div className="mt-4 rounded-[16px] border border-amber-100 bg-amber-50 px-4 py-3 text-left text-xs leading-5 text-amber-800">
            <div className="flex items-start gap-2">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                Users will not see this video after deletion. Use inactive instead if you only want to pause it.
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="inline-flex h-[44px] items-center justify-center rounded-[14px] border bg-white px-5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            style={{ borderColor: "#e7eaf0" }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex h-[44px] items-center justify-center gap-2 rounded-[14px] bg-red-500 px-5 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(239,68,68,0.18)] transition hover:-translate-y-0.5 hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            {deleting ? "Deleting..." : "Delete video"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function hashText(value = "") {
  const textValue = String(value || "short-video");
  let hash = 0;
  for (let i = 0; i < textValue.length; i += 1) {
    hash = (hash * 31 + textValue.charCodeAt(i)) % 100000;
  }
  return Math.abs(hash || 721);
}

function pickBySeed(list, seed, offset = 0) {
  if (!Array.isArray(list) || !list.length) return "-";
  return list[Math.abs(seed + offset) % list.length];
}

function formatStatsDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getWarehouseLabelFromValue(value, filterOptions = {}) {
  const warehouses = filterOptions.warehouses || [];
  const match = warehouses.find((warehouse) => String(warehouse.value) === String(value));
  return match?.label || value;
}

function toNumberValue(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeBreakdownRows(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row && row.label)
    .map((row) => ({ label: String(row.label), value: toNumberValue(row.value) }));
}

function normalizeUserRows(rows = []) {
  const seen = new Set();
  const out = [];

  for (const row of Array.isArray(rows) ? rows : []) {
    const key = String(row.id || row.userId || row.sessionId || `${row.name}-${row.viewedAt}` || "").trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: key,
      name: row.name || row.userName || row.email || row.phone || "User",
      city: row.city || "-",
      state: row.state || "-",
      warehouse: row.warehouse || row.warehouseName || "-",
      device: row.device || row.platform || "Unknown",
      viewedAt: row.viewedAt || row.lastEventAt || row.createdAt || row.created_at,
      watchTime: row.watchTime || (row.watchSeconds ? `${Math.round(Number(row.watchSeconds))}s` : "-"),
      action: row.action || "Viewed",
    });
  }

  return out;
}

function buildVideoStats(item, backendStats = null) {
  const raw = backendStats?.stats || backendStats || {};
  const views = toNumberValue(raw.views);
  const clicks = toNumberValue(raw.clicks);
  const completed = toNumberValue(raw.completed ?? raw.completes);
  const uniqueUsers = toNumberValue(raw.uniqueUsers ?? raw.unique_users);
  const avgWatchSeconds = toNumberValue(raw.avgWatchSeconds ?? raw.avg_watch_seconds);
  const completionRate = views ? Math.round((completed / views) * 100) : 0;
  const clickRate = views ? Math.round((clicks / views) * 100) : 0;

  return {
    views,
    clicks,
    completed,
    uniqueUsers,
    avgWatchSeconds,
    completionRate: toNumberValue(raw.completionRate, completionRate),
    clickRate: toNumberValue(raw.clickRate, clickRate),
    users: normalizeUserRows(raw.users),
    cityBreakdown: normalizeBreakdownRows(raw.cityBreakdown),
    stateBreakdown: normalizeBreakdownRows(raw.stateBreakdown),
    warehouseBreakdown: normalizeBreakdownRows(raw.warehouseBreakdown),
    timeSlots: normalizeBreakdownRows(raw.timeSlots),
    lastEventAt: raw.last_event_at || raw.lastEventAt || null,
  };
}

function MiniMetricCard({ label, value, helper, icon: Icon, tone = "pink" }) {
  const toneClasses = {
    pink: "bg-[#fff1f3] text-[#d86a78]",
    green: "bg-emerald-50 text-emerald-600",
    blue: "bg-[#eef3ff] text-[#4f7cff]",
    amber: "bg-[#fff5e8] text-[#f59e0b]",
    slate: "bg-slate-100 text-slate-600",
  };

  return (
    <div className="rounded-[20px] border bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.04)]" style={{ borderColor: "#e8edf4" }}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-bold uppercase tracking-[0.05em] text-slate-400">{label}</div>
        {Icon ? (
          <div className={`flex h-9 w-9 items-center justify-center rounded-full ${toneClasses[tone] || toneClasses.pink}`}>
            <Icon size={18} />
          </div>
        ) : null}
      </div>
      <div className="mt-3 text-[28px] font-bold leading-none text-slate-950">{value}</div>
      {helper ? <div className="mt-2 text-xs font-medium text-slate-500">{helper}</div> : null}
    </div>
  );
}

function BreakdownList({ title, items = [] }) {
  const max = Math.max(...items.map((item) => Number(item.value || 0)), 1);

  return (
    <div className="rounded-[20px] border bg-white p-4" style={{ borderColor: "#e8edf4" }}>
      <div className="mb-4 text-sm font-bold text-slate-900">{title}</div>
      <div className="max-h-[280px] space-y-3 overflow-y-auto pr-1">
        {items.map((item) => (
          <div key={item.label}>
            <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
              <span className="max-w-[180px] truncate font-semibold text-slate-600">{item.label}</span>
              <span className="font-bold text-slate-900">{item.value}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-[#d86a78]"
                style={{ width: `${Math.max(8, Math.min(100, (Number(item.value || 0) / max) * 100))}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShortVideoStatsModal({ item, stats, filterOptions, onClose }) {
  const analytics = useMemo(
    () => buildVideoStats(item, stats, filterOptions),
    [item, stats, filterOptions]
  );

  return (
    <Modal
      open={Boolean(item)}
      title="Short video stats"
      subtitle="Real user analytics from short video tracking. Duplicate user rows are merged."
      width="max-w-[1040px]"
      onClose={onClose}
    >
      <div className="space-y-5 p-5 sm:p-6">
        <div className="flex flex-col gap-3 rounded-[22px] border bg-[#fffafb] p-4 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: BORDER }}>
          <div>
            <div className="text-lg font-bold text-slate-950">{item?.title || "Untitled video"}</div>
            <div className="mt-1 text-sm leading-6 text-slate-500">
              {item?.description || "No description"}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Pill tone="pink">Order {item?.displayOrder ?? 100}</Pill>
              <Pill tone="slate">{item?.targetType === "FILTERED" ? "Filtered users" : "All users"}</Pill>
              <Pill tone={statusTone(item?.computedStatus)}>{item?.computedStatus || (item?.isActive ? "ACTIVE" : "INACTIVE")}</Pill>
            </div>
          </div>
          <div className="rounded-[16px] border border-amber-100 bg-amber-50 px-4 py-3 text-xs font-semibold leading-5 text-amber-800">
            Real tracking data. One row per user/session, not every event.
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <MiniMetricCard label="Views" value={analytics.views} helper="Total plays" icon={Eye} tone="pink" />
          <MiniMetricCard label="Clicks" value={analytics.clicks} helper={`${analytics.clickRate}% CTR`} icon={Link2} tone="blue" />
          <MiniMetricCard label="Completed" value={analytics.completed} helper={`${analytics.completionRate}% completion`} icon={CheckCircle2} tone="green" />
          <MiniMetricCard label="Users" value={analytics.uniqueUsers} helper="Unique users" icon={Users} tone="slate" />
          <MiniMetricCard label="Avg watch" value={`${analytics.avgWatchSeconds}s`} helper="Per play" icon={Clock3} tone="amber" />
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <BreakdownList title="Top cities" items={analytics.cityBreakdown} />
          <BreakdownList title="Top states" items={analytics.stateBreakdown} />
          <BreakdownList title="Warehouse performance" items={analytics.warehouseBreakdown} />
        </div>

        <div className="grid gap-4 xl:grid-cols-[1fr_1.25fr]">
          <BreakdownList title="Views by time" items={analytics.timeSlots} />

          <div className="rounded-[20px] border bg-white" style={{ borderColor: "#e8edf4" }}>
            <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: "#eef2f7" }}>
              <div className="text-sm font-bold text-slate-900">Users who watched</div>
              <Pill tone="pink">User rows</Pill>
            </div>

            <div className="max-h-[330px] overflow-auto">
              <table className="min-w-full text-left">
                <thead className="sticky top-0 bg-white text-xs text-slate-400">
                  <tr className="border-b" style={{ borderColor: "#eef2f7" }}>
                    <th className="px-4 py-3 font-bold">User</th>
                    <th className="px-4 py-3 font-bold">Location</th>
                    <th className="px-4 py-3 font-bold">Warehouse</th>
                    <th className="px-4 py-3 font-bold">Date & time</th>
                    <th className="px-4 py-3 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {analytics.users.map((user) => (
                    <tr key={user.id} className="text-sm">
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-800">{user.name}</div>
                        <div className="text-xs text-slate-400">{user.device} · {user.watchTime}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{user.city}</div>
                        <div className="text-xs text-slate-400">{user.state}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{user.warehouse}</td>
                      <td className="px-4 py-3 text-slate-600">{formatStatsDateTime(user.viewedAt)}</td>
                      <td className="px-4 py-3">
                        <Pill tone={user.action === "Clicked" ? "blue" : user.action === "Completed" ? "green" : "slate"}>
                          {user.action}
                        </Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}


function ShortVideoStatsDetailPage({ item, stats, filterOptions, videos = [], statsFilters = {}, loadingStats = false, onFilterChange, onBack }) {
  const analytics = useMemo(
    () => buildVideoStats(item, stats, filterOptions),
    [item, stats, filterOptions]
  );

  const summaryRows = [
    { label: "Video", value: item?.title || "Untitled video" },
    { label: "Status", value: item?.computedStatus || (item?.isActive ? "ACTIVE" : "INACTIVE") },
    { label: "Display order", value: item?.displayOrder ?? 100 },
    { label: "Target", value: item?.targetType === "FILTERED" ? "Filtered users" : "All users" },
    { label: "Section", value: (item?.filters?.contexts || []).join(" + ") || "Grocery" },
    { label: "Schedule", value: `${normalizeDate(item?.startDate) || "-"} to ${normalizeDate(item?.endDate) || "-"}` },
  ];

  const locationRows = [
    ...analytics.cityBreakdown.map((row) => ({ type: "City", ...row })),
    ...analytics.stateBreakdown.map((row) => ({ type: "State", ...row })),
  ];

  const videoOptions = [
    ...normalizeOptionList(videos).map((video) => ({
      value: video.value,
      label: videos.find((item) => String(item.id) === String(video.value))?.title || video.label,
    })),
  ].filter((option) => option.value);

  const warehouseOptions = [{ label: "All warehouses", value: "" }, ...normalizeOptionList(filterOptions.warehouses)];
  const stateOptions = [{ label: "All states", value: "" }, ...normalizeOptionList(filterOptions.states)];

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden border-0 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
        <div className="px-4 py-4 sm:px-5 lg:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <button
                type="button"
                onClick={onBack}
                className="mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] border bg-white text-slate-600 transition hover:bg-[#fff4f6] hover:text-[#d86a78]"
                style={{ borderColor: "#e7eaf0" }}
                title="Back to short videos"
              >
                <ChevronLeft size={21} />
              </button>

              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#fff4f6] px-3 py-1.5 text-xs font-semibold text-[#d86a78]">
                  <Eye size={16} />
                  Short Video Analytics
                </div>

                <h1 className="mt-3 text-[24px] font-bold leading-tight text-slate-900 sm:text-[30px]">
                  {item?.title || "Short video details"}
                </h1>

                <p className="mt-1 max-w-[860px] text-sm leading-6 text-slate-500">
                  Detailed real stats for users, cities, states, warehouses, date/time, watch time, clicks, and completion. Duplicate events are merged into one user row.
                </p>
              </div>
            </div>

            {/* <div className="rounded-[16px] border border-amber-100 bg-amber-50 px-4 py-3 text-xs font-semibold leading-5 text-amber-800">
              Real tracking data with duplicate users merged.
            </div> */}
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">Stats filters</div>
            <div className="mt-1 text-xs text-slate-500">Filter by video ad, warehouse, state, and date. User rows stay unique after filtering.</div>
          </div>
          {loadingStats ? (
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#d86a78]">
              <Loader2 size={15} className="animate-spin" /> Loading stats
            </div>
          ) : null}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <InlineSelect
            label="Video ad"
            value={String(item?.id || "")}
            options={videoOptions.length ? videoOptions : [{ label: item?.title || "Current video", value: String(item?.id || "") }]}
            onChange={(value) => onFilterChange?.({ videoId: value })}
            minWidth={180}
            menuMaxHeight={280}
          />
          <InlineSelect
            label="Warehouse"
            value={statsFilters.warehouseId || ""}
            options={warehouseOptions}
            onChange={(value) => onFilterChange?.({ warehouseId: value })}
            minWidth={180}
            menuMaxHeight={280}
          />
          <InlineSelect
            label="State"
            value={statsFilters.state || ""}
            options={stateOptions}
            onChange={(value) => onFilterChange?.({ state: value })}
            minWidth={160}
            menuMaxHeight={280}
          />
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.04em] text-slate-400">From date</span>
            <input
              type="date"
              value={statsFilters.fromDate || ""}
              onChange={(event) => onFilterChange?.({ fromDate: event.target.value })}
              className="h-11 w-full rounded-[14px] border bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-[#d86a78]"
              style={{ borderColor: "#e7eaf0" }}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.04em] text-slate-400">To date</span>
            <input
              type="date"
              value={statsFilters.toDate || ""}
              onChange={(event) => onFilterChange?.({ toDate: event.target.value })}
              className="h-11 w-full rounded-[14px] border bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-[#d86a78]"
              style={{ borderColor: "#e7eaf0" }}
            />
          </label>
        </div>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <MiniMetricCard label="Views" value={analytics.views} helper="Total plays" icon={Eye} tone="pink" />
        <MiniMetricCard label="Clicks" value={analytics.clicks} helper={`${analytics.clickRate}% CTR`} icon={Link2} tone="blue" />
        <MiniMetricCard label="Completed" value={analytics.completed} helper={`${analytics.completionRate}% completion`} icon={CheckCircle2} tone="green" />
        <MiniMetricCard label="Users" value={analytics.uniqueUsers} helper="Unique users" icon={Users} tone="slate" />
        <MiniMetricCard label="Avg watch" value={`${analytics.avgWatchSeconds}s`} helper="Per play" icon={Clock3} tone="amber" />
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.8fr_1.2fr]">
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="text-base font-bold text-slate-900">Video summary</div>
            <Pill tone={statusTone(item?.computedStatus)}>{item?.computedStatus || (item?.isActive ? "ACTIVE" : "INACTIVE")}</Pill>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
            {summaryRows.map((row) => (
              <div key={row.label} className="rounded-[16px] border bg-[#fcfcfd] px-4 py-3" style={{ borderColor: "#eef2f7" }}>
                <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-slate-400">{row.label}</div>
                <div className="mt-1 break-words text-sm font-semibold text-slate-800">{row.value}</div>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <BreakdownList title="Top cities" items={analytics.cityBreakdown} />
          <BreakdownList title="Top states" items={analytics.stateBreakdown} />
          <BreakdownList title="Warehouse performance" items={analytics.warehouseBreakdown} />
          <BreakdownList title="Views by time" items={analytics.timeSlots} />
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.35fr_0.65fr]">
        <Card className="overflow-hidden">
          <div className="flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: "#eef2f7" }}>
            <div>
              <div className="text-base font-bold text-slate-900">Users who watched</div>
              <div className="mt-1 text-xs text-slate-500">Scrollable table for user, city, state, warehouse, date/time, device, watch time, and action.</div>
            </div>
            <Pill tone="pink">{analytics.users.length} user rows</Pill>
          </div>

          <div className="max-h-[520px] overflow-auto">
            <table className="min-w-[980px] w-full text-left">
              <thead className="sticky top-0 z-10 bg-white text-xs text-slate-400 shadow-[0_1px_0_#eef2f7]">
                <tr>
                  <th className="px-5 py-3 font-bold">User</th>
                  <th className="px-5 py-3 font-bold">City</th>
                  <th className="px-5 py-3 font-bold">State</th>
                  <th className="px-5 py-3 font-bold">Warehouse</th>
                  <th className="px-5 py-3 font-bold">Date & time</th>
                  <th className="px-5 py-3 font-bold">Device</th>
                  <th className="px-5 py-3 font-bold">Watched</th>
                  <th className="px-5 py-3 font-bold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {analytics.users.map((user) => (
                  <tr key={user.id} className="text-sm transition hover:bg-[#fcfcfd]">
                    <td className="px-5 py-4 font-bold text-slate-800">{user.name}</td>
                    <td className="px-5 py-4 text-slate-600">{user.city}</td>
                    <td className="px-5 py-4 text-slate-600">{user.state}</td>
                    <td className="px-5 py-4 text-slate-600">{user.warehouse}</td>
                    <td className="px-5 py-4 text-slate-600">{formatStatsDateTime(user.viewedAt)}</td>
                    <td className="px-5 py-4 text-slate-600">{user.device}</td>
                    <td className="px-5 py-4 text-slate-600">{user.watchTime}</td>
                    <td className="px-5 py-4">
                      <Pill tone={user.action === "Clicked" ? "blue" : user.action === "Completed" ? "green" : "slate"}>
                        {user.action}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="overflow-hidden">
            <div className="border-b px-5 py-4" style={{ borderColor: "#eef2f7" }}>
              <div className="text-base font-bold text-slate-900">Location summary</div>
              <div className="mt-1 text-xs text-slate-500">Cities and states are scrollable when data grows.</div>
            </div>

            <div className="max-h-[260px] overflow-auto">
              <table className="min-w-full text-left">
                <thead className="sticky top-0 bg-white text-xs text-slate-400 shadow-[0_1px_0_#eef2f7]">
                  <tr>
                    <th className="px-5 py-3 font-bold">Type</th>
                    <th className="px-5 py-3 font-bold">Name</th>
                    <th className="px-5 py-3 text-right font-bold">Views</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {locationRows.map((row, index) => (
                    <tr key={`${row.type}-${row.label}-${index}`} className="text-sm">
                      <td className="px-5 py-3 text-slate-500">{row.type}</td>
                      <td className="px-5 py-3 font-semibold text-slate-800">{row.label}</td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="border-b px-5 py-4" style={{ borderColor: "#eef2f7" }}>
              <div className="text-base font-bold text-slate-900">Warehouse list</div>
              <div className="mt-1 text-xs text-slate-500">Separate scroll for many warehouses.</div>
            </div>

            <div className="max-h-[260px] overflow-auto">
              <table className="min-w-full text-left">
                <thead className="sticky top-0 bg-white text-xs text-slate-400 shadow-[0_1px_0_#eef2f7]">
                  <tr>
                    <th className="px-5 py-3 font-bold">Warehouse</th>
                    <th className="px-5 py-3 text-right font-bold">Views</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {analytics.warehouseBreakdown.map((row) => (
                    <tr key={row.label} className="text-sm">
                      <td className="px-5 py-3 font-semibold text-slate-800">{row.label}</td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}


export default function ShortVideosPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ warehouses: [], cities: [], states: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState("");
  const [context, setContext] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [tab, setTab] = useState("videos");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [conflicts, setConflicts] = useState([]);
  const [statsPageItem, setStatsPageItem] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsFilters, setStatsFilters] = useState({ warehouseId: "", state: "", fromDate: "", toDate: "" });
  const [loadingStats, setLoadingStats] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [historyDeleting, setHistoryDeleting] = useState(false);
  const [historyDeleteError, setHistoryDeleteError] = useState("");
  const [confirmingClearHistory, setConfirmingClearHistory] = useState(false);
  const [historyRowConfirmId, setHistoryRowConfirmId] = useState(null);

  const [pageSize, setPageSize] = useState(10);
  const limit = pageSize;
  const totalPages = Math.max(Math.ceil(total / limit), 1);
  const pageStart = items.length ? (page - 1) * limit + 1 : 0;
  const pageEnd = Math.min(page * limit, total);

  async function loadVideos(nextPage = page) {
    setLoading(true);
    setError("");
    try {
      const [listRes, summaryRes] = await Promise.all([
        shortVideosService.list({ page: nextPage, limit, q, status, context, warehouseId: warehouseFilter || undefined, includeDeleted }),
        shortVideosService.summary(),
      ]);
      setItems(listRes.items || []);
      setTotal(Number(listRes.total || 0));
      setPage(Number(listRes.page || nextPage));
      setSummary(summaryRes.data || null);
    } catch (err) {
      setError(readApiError(err, "Unable to load short videos"));
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    try {
      setHistoryDeleteError("");
      const res = await shortVideosService.history({ page: 1, limit: 50 });
      setHistory(res.items || []);
      setConfirmingClearHistory(false);
      setHistoryRowConfirmId(null);
    } catch (err) {
      setError(readApiError(err, "Unable to load history"));
    }
  }

  async function loadOptions() {
    try {
      const res = await shortVideosService.getFilterOptions();
      setFilterOptions(res.data || { warehouses: [], cities: [], states: [] });
    } catch (err) {
      console.warn("Short video filter options failed", err);
    }
  }

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    loadVideos(1);
  }, [status, context, warehouseFilter, includeDeleted, q, pageSize]);

  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setConflicts([]);
    setError("");
    setNotice("");
    setModalOpen(true);
  }

  function closeEditor() {
    setModalOpen(false);
    setError("");
  }

  function openEdit(item) {
    setEditing(item);
    setForm(itemToForm(item));
    setConflicts([]);
    setError("");
    setNotice("");
    setModalOpen(true);
  }

  async function checkConflicts() {
    setError("");
    try {
      const payload = buildVideoFormPayload(form);
      const res = await shortVideosService.conflicts({ ...payload, id: editing?.id || undefined });
      setConflicts(res.items || []);
      if (!res.items?.length) setNotice("No time conflict found.");
    } catch (err) {
      setError(readApiError(err, "Conflict check failed"));
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");
    try {
      if (!editing && !form.video) throw new Error("Video file is required");

      const payload = buildVideoFormPayload(form);
      if (payload.targetType === "FILTERED" && payload.filters.warehouseIds.length === 0) {
        throw new Error("Select at least one warehouse for filtered targeting");
      }

      const res = editing
        ? await shortVideosService.update(editing.id, payload)
        : await shortVideosService.create(payload);
      setConflicts(res.conflicts || []);
      setNotice((res.conflicts || []).length ? "Video saved. Conflict found, so slider order will decide priority." : "Short video saved successfully.");
      setModalOpen(false);
      await loadVideos(page);
      if (tab === "history") await loadHistory();
    } catch (err) {
      setError(readApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus(item) {
    setError("");
    try {
      await shortVideosService.setStatus(item.id, !item.isActive);
      setNotice(!item.isActive ? "Video activated." : "Video deactivated.");
      await loadVideos(page);
    } catch (err) {
      setError(readApiError(err, "Status update failed"));
    }
  }

  async function removeVideo(item) {
    if (!item?.id) return;
    setDeleting(true);
    setDeleteError("");
    setError("");
    try {
      await shortVideosService.remove(item.id);
      setNotice("Video deleted and saved in history.");
      setDeleteTarget(null);
      await loadVideos(page);
    } catch (err) {
      setDeleteError(readApiError(err, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  }

  async function clearHistory() {
    setHistoryDeleteError("");

    if (!confirmingClearHistory) {
      setConfirmingClearHistory(true);
      setHistoryRowConfirmId(null);
      return;
    }

    setHistoryDeleting(true);
    try {
      await shortVideosService.deleteHistory({ confirm: true });
      setHistory([]);
      setNotice("Short video history cleared.");
      setConfirmingClearHistory(false);
      setHistoryRowConfirmId(null);
    } catch (err) {
      setHistoryDeleteError(readApiError(err, "Unable to clear history"));
    } finally {
      setHistoryDeleting(false);
    }
  }

  async function deleteHistoryRow(row) {
    if (!row?.id) return;
    setHistoryDeleteError("");

    if (String(historyRowConfirmId) !== String(row.id)) {
      setHistoryRowConfirmId(row.id);
      setConfirmingClearHistory(false);
      return;
    }

    setHistoryDeleting(true);
    try {
      await shortVideosService.deleteHistoryRow(row.id);
      setHistory((prev) => prev.filter((item) => String(item.id) !== String(row.id)));
      setNotice("History row deleted.");
      setHistoryRowConfirmId(null);
    } catch (err) {
      setHistoryDeleteError(readApiError(err, "Unable to delete history row"));
    } finally {
      setHistoryDeleting(false);
    }
  }

  async function loadStatsForItem(item, filters = statsFilters) {
    if (!item?.id) return;
    setLoadingStats(true);
    try {
      const res = await shortVideosService.stats(item.id, filters);
      setStats(res.data || null);
      if (res.data?.video) {
        setStatsPageItem((prev) => ({ ...(prev || item), ...res.data.video }));
      }
    } catch (err) {
      setError(readApiError(err, "Stats load failed"));
    } finally {
      setLoadingStats(false);
    }
  }

  async function openStats(item) {
    const initialFilters = { warehouseId: "", state: "", fromDate: "", toDate: "" };
    setStatsPageItem(item);
    setStats(null);
    setStatsFilters(initialFilters);
    await loadStatsForItem(item, initialFilters);
  }

  async function handleStatsFilterChange(patch = {}) {
    const selectedVideoId = patch.videoId || statsPageItem?.id;
    const nextItem = items.find((row) => String(row.id) === String(selectedVideoId)) || statsPageItem;
    const nextFilters = {
      ...statsFilters,
      ...patch,
    };
    delete nextFilters.videoId;
    setStatsFilters(nextFilters);
    if (nextItem && String(nextItem.id) !== String(statsPageItem?.id)) {
      setStatsPageItem(nextItem);
    }
    await loadStatsForItem(nextItem, nextFilters);
  }

  const handleSearch = () => {
    setPage(1);
    setQ(searchInput.trim());
  };

  const handleReset = () => {
    setSearchInput("");
    setQ("");
    setStatus("");
    setContext("");
    setWarehouseFilter("");
    setIncludeDeleted(false);
    setPage(1);
  };

  const statsCards = [
    {
      label: "Total Videos",
      value: summary?.totalVideos || 0,
      helper: "Video library overview",
      icon: LayoutGrid,
      borderColor: "#f7d7dc",
      iconClassName: "bg-[#fff1f3] text-[#d86a78]",
      surfaceClassName: "bg-[linear-gradient(135deg,#ffffff_30%,#fff3f6_100%)]",
      waveClassName: "text-rose-100",
    },
    {
      label: "Active Now",
      value: summary?.activeVideos || 0,
      helper: "Currently visible in app",
      icon: Radio,
      borderColor: "#d8f0e3",
      iconClassName: "bg-[#edf9f1] text-emerald-600",
      surfaceClassName: "bg-[linear-gradient(135deg,#ffffff_30%,#effcf6_100%)]",
      waveClassName: "text-emerald-100",
    },
    {
      label: "Scheduled",
      value: summary?.scheduledVideos || 0,
      helper: "Upcoming schedule queue",
      icon: CalendarDays,
      borderColor: "#dfe7fb",
      iconClassName: "bg-[#eef3ff] text-[#4f7cff]",
      surfaceClassName: "bg-[linear-gradient(135deg,#ffffff_30%,#f0f4ff_100%)]",
      waveClassName: "text-indigo-100",
    },
    {
      label: "Inactive",
      value: summary?.inactiveVideos || 0,
      helper: "Paused or unavailable videos",
      icon: FileText,
      borderColor: "#fde7c8",
      iconClassName: "bg-[#fff5e8] text-[#f59e0b]",
      surfaceClassName: "bg-[linear-gradient(135deg,#ffffff_30%,#fff7eb_100%)]",
      waveClassName: "text-orange-100",
    },
  ];

  const warehouseOptions = useMemo(
    () => [{ label: "All Warehouses", value: "" }, ...normalizeOptionList(filterOptions.warehouses)],
    [filterOptions.warehouses]
  );

  const visibleItems = useMemo(() => {
    const selectedWarehouseId = toOptionValue(warehouseFilter);
    if (!selectedWarehouseId) return items;

    return items.filter((item) => getVideoWarehouseIds(item).includes(selectedWarehouseId));
  }, [items, warehouseFilter]);

  const visibleTotal = total;
  const visiblePageStart = visibleItems.length ? pageStart : 0;
  const visiblePageEnd = visibleItems.length ? Math.min(pageStart + visibleItems.length - 1, total) : 0;
  const visibleTotalPages = totalPages;

  const handlePageSizeChange = (nextValue) => {
    const nextSize = Number(nextValue) || 10;
    setPage(1);
    setPageSize(nextSize);
  };

  if (statsPageItem) {
    return (
      <ShortVideoStatsDetailPage
        item={statsPageItem}
        stats={stats}
        filterOptions={filterOptions}
        videos={items}
        statsFilters={statsFilters}
        loadingStats={loadingStats}
        onFilterChange={handleStatsFilterChange}
        onBack={() => {
          setStatsPageItem(null);
          setStats(null);
          setStatsFilters({ warehouseId: "", state: "", fromDate: "", toDate: "" });
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card className="relative overflow-hidden border-0 bg-[linear-gradient(112deg,#ffffff_0%,#fffefe_52%,#fff0f5_100%)] shadow-[0_18px_60px_rgba(15,23,42,0.07)]">
        <div className="pointer-events-none absolute -right-16 -top-28 h-64 w-64 rounded-full bg-rose-200/30 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-[28%] h-24 w-64 rounded-full bg-pink-100/50 blur-2xl" />

        <div className="relative min-h-[142px] px-5 py-4 sm:px-6 lg:px-7">
          <div className="flex h-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative z-10 min-w-0 max-w-[700px]">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#fff4f6] px-3 py-1.5 text-xs font-semibold text-[#d86a78]">
                <Film size={16} />
                Short Video Ads
              </div>

              <h1 className="mt-3 text-[24px] font-bold leading-tight text-[#d86a78] sm:text-[28px]">
                Short video module
              </h1>

              <p className="mt-1 max-w-[680px] text-[13px] leading-5 text-slate-500">
                Videos are scheduled by warehouse/date/time and served to the user app as compressed HLS playback. Do not use poster page for this flow.
              </p>
            </div>

            <button
              type="button"
              onClick={openCreate}
              className="relative z-20 inline-flex h-[42px] items-center justify-center gap-2 self-start rounded-[14px] px-5 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(216,106,120,0.25)] transition hover:-translate-y-0.5 lg:self-center"
              style={{ backgroundColor: PINK }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = PINK_HOVER)}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = PINK)}
            >
              <Plus size={18} />
              Add short video
            </button>
          </div>

          <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-[205px] hidden h-full w-[360px] lg:block">
            <div className="absolute left-4 top-7 rotate-[-5deg] text-[17px] font-semibold italic leading-tight text-[#ed5e77]">Engage<br />More Users<div className="mt-1 h-0.5 w-20 rotate-[-7deg] rounded-full bg-[#ed5e77]" /></div>
            <div className="absolute bottom-[-45px] left-[145px] h-[176px] w-[88px] rotate-[-8deg] rounded-[24px] border-[6px] border-[#27345e] bg-[linear-gradient(160deg,#dbe4ff,#f4b9cd)] shadow-[0_18px_35px_rgba(34,49,91,0.20)]">
              <div className="mx-auto mt-1 h-1.5 w-8 rounded-full bg-[#27345e]" />
              <div className="mx-auto mt-10 grid h-12 w-12 place-items-center rounded-[14px] bg-[#f55c7a] text-white shadow-lg"><PlaySquare size={27} fill="currentColor" /></div>
            </div>
            <div className="absolute right-1 top-8 flex h-12 w-24 rotate-[-10deg] items-center gap-2 rounded-[14px] bg-white/95 px-3 shadow-[0_12px_28px_rgba(37,48,78,0.13)]"><span className="grid h-7 w-7 place-items-center rounded-full bg-rose-100 text-[#ec5a73]"><PlaySquare size={15} fill="currentColor" /></span><span className="space-y-1"><i className="block h-1.5 w-8 rounded-full bg-rose-200" /><i className="block h-1.5 w-5 rounded-full bg-slate-200" /></span></div>
          </div>
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {statsCards.map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
      </div>

      {!modalOpen ? <ErrorNotice message={error} onClose={() => setError("")} /> : null}
      {notice ? (
        <div className="rounded-[18px] border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">
          {notice}
        </div>
      ) : null}

      <Card className="overflow-hidden border-0 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
        <div
          className="flex flex-col gap-3 border-b bg-[#fcfcfd] px-4 py-3 sm:px-5"
          style={{ borderColor: "#eef2f7" }}
        >
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-col gap-2 lg:flex-row lg:items-center">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTab("videos")}
                  className={`h-[40px] rounded-[12px] px-4 text-[13px] font-semibold transition ${tab === "videos" ? "bg-[#fff0f3] text-[#d86a78]" : "text-slate-500 hover:bg-slate-100"}`}
                >
                  Videos
                </button>
                <button
                  type="button"
                  onClick={() => setTab("history")}
                  className={`h-[40px] whitespace-nowrap rounded-[12px] px-3 text-[13px] font-semibold transition ${tab === "history" ? "bg-[#fff0f3] text-[#d86a78]" : "text-slate-500 hover:bg-slate-100"}`}
                >
                  All history
                </button>
              </div>

              {tab === "videos" ? (
                <>
                  <div className="relative w-full xl:max-w-[300px]">
                    <Search
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                      size={18}
                    />
                    <input
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                      placeholder="Search title/product"
                      className="h-[40px] w-full rounded-[12px] border bg-white pl-11 pr-4 text-[13px] text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-[#f7d4da]"
                      style={{ borderColor: "#e7eaf0" }}
                    />
                  </div>

                  <InlineSelect
                    value={context}
                    minWidth={140}
                    options={[{ label: "All Sections", value: "" }, ...CONTEXT_OPTIONS]}
                    onChange={(nextValue) => {
                      setPage(1);
                      setContext(nextValue);
                    }}
                  />

                  <InlineSelect
                    value={warehouseFilter}
                    minWidth={170}
                    options={warehouseOptions}
                    onChange={(nextValue) => {
                      setPage(1);
                      setWarehouseFilter(nextValue);
                    }}
                  />

                  <InlineSelect
                    value={status}
                    minWidth={125}
                    options={STATUS_OPTIONS}
                    onChange={(nextValue) => {
                      setPage(1);
                      setStatus(nextValue);
                    }}
                  />
                </>
              ) : null}
            </div>

            {tab === "videos" ? (
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex h-[40px] items-center gap-2 rounded-[12px] border bg-white px-3 text-[12px] font-semibold text-slate-600" style={{ borderColor: "#e7eaf0" }}>
                  <input
                    type="checkbox"
                    checked={includeDeleted}
                    onChange={(e) => {
                      setPage(1);
                      setIncludeDeleted(e.target.checked);
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-[#d86a78] focus:ring-[#f7d4da]"
                  />
                  Include deleted
                </label>

                <button
                  type="button"
                  onClick={handleSearch}
                  className="inline-flex h-[40px] items-center justify-center gap-2 rounded-[12px] border border-rose-100 bg-rose-50 px-3 text-[12px] font-semibold text-[#d86a78] transition hover:bg-rose-100"
                  style={{ borderColor: "#e7eaf0" }}
                >
                  <Search size={16} />
                  Search
                </button>

                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex h-[40px] items-center justify-center gap-2 rounded-[12px] border bg-white px-3 text-[12px] font-semibold text-slate-600 transition hover:bg-slate-50"
                  style={{ borderColor: "#e7eaf0" }}
                >
                  <X size={16} />
                  Reset
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {tab === "videos" ? (
          <div className="table-scrollbar overflow-x-auto">
            <table className="w-full min-w-[1080px] table-fixed text-left">
              <colgroup>
                <col />
                <col style={{ width: "17%" }} />
                <col style={{ width: "19%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "112px" }} />
                <col style={{ width: "220px" }} />
              </colgroup>
              <thead>
                <tr className="border-b bg-white text-slate-500" style={{ borderColor: "#eef2f7" }}>
                  <th className="px-4 py-3 text-[13px] font-semibold">Video</th>
                  <th className="px-4 py-3 text-[13px] font-semibold">Target Audience</th>
                  <th className="px-4 py-3 text-[13px] font-semibold">Schedule</th>
                  <th className="px-4 py-3 text-[13px] font-semibold">Playback</th>
                  <th className="px-4 py-3 text-[13px] font-semibold">Status</th>
                  <th className="px-4 py-3 text-center text-[13px] font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-10 text-center text-sm text-slate-500">
                      <Loader2 className="mx-auto mb-2 animate-spin" />
                      Loading videos...
                    </td>
                  </tr>
                ) : visibleItems.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-10 text-center">
                      <EmptyState />
                    </td>
                  </tr>
                ) : (
                  visibleItems.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b align-middle transition hover:bg-[#fffafb]"
                      style={{ borderColor: "#eef2f7" }}
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className="flex h-[56px] w-[78px] shrink-0 items-center justify-center overflow-hidden rounded-[12px] border bg-slate-50 shadow-sm"
                            style={{ borderColor: "#edf0f5" }}
                          >
                            {item.thumbnailUrl ? (
                              <img
                                alt={item.title || "video thumbnail"}
                                src={buildShortVideoAssetUrl(item.thumbnailUrl)}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <PlaySquare className="text-slate-400" size={24} />
                            )}
                          </div>

                          <div className="min-w-0">
                            <div className="break-words text-[15px] font-semibold text-slate-800">{item.title || "-"}</div>
                            <div className="mt-0.5 line-clamp-1 max-w-[340px] text-[13px] leading-5 text-slate-500">
                              {item.description || "No description"}
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              <Pill tone="pink">Order {item.displayOrder}</Pill>
                              <Pill>{item.redirectType || "NONE"}</Pill>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2.5">
                        <div className="min-w-0 space-y-1">
                          <div
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold ${item.targetType === "FILTERED" ? "bg-[#eef3ff] text-[#4f7cff]" : "bg-slate-100 text-slate-600"}`}
                          >
                            {item.targetType === "FILTERED" ? <MapPin size={13} /> : <Globe2 size={13} />}
                            {item.targetType === "FILTERED" ? "Filtered" : "All Users"}
                          </div>
                          <div className="truncate text-[12px] leading-5 text-slate-500">
                            Section: {(item.filters?.contexts || []).join(" + ") || "Grocery"}
                          </div>
                          <div className="text-xs font-medium text-slate-400">
                            WH: {item.filters?.warehouseIds?.length || 0}
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2.5">
                        <div className="min-w-0 text-[12px] text-slate-500">
                          <div className="flex items-center gap-2 font-semibold text-slate-700">
                            <CalendarDays size={14} />
                            {normalizeDate(item.startDate) || "-"} – {normalizeDate(item.endDate) || "-"}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <Clock3 size={14} />
                            {normalizeTime(item.dailyStartTime) || "--:--"} – {normalizeTime(item.dailyEndTime) || "--:--"}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            Days: {(item.daysOfWeek || []).join(", ") || "-"}
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2.5">
                        <div className="min-w-0 text-[12px] text-slate-500">
                          <div className="font-semibold text-slate-700">
                            {item.playbackType || (item.hlsUrl ? "HLS" : "MP4_RANGE")}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            Original {formatBytes(item.fileSize)}
                          </div>
                          <div className="mt-1 text-xs text-slate-400">
                            Processed {formatBytes(item.processedFileSize)}
                          </div>
                          {item.processingStatus ? (
                            <div className="mt-2">
                              <Pill tone={item.processingStatus === "READY" ? "green" : item.processingStatus === "FAILED" ? "red" : "amber"}>
                                {item.processingStatus}
                              </Pill>
                            </div>
                          ) : null}
                          {item.processingError ? (
                            <div className="mt-1 line-clamp-2 text-xs text-red-500">{item.processingError}</div>
                          ) : null}
                        </div>
                      </td>

                      <td className="px-3 py-2.5">
                        <Pill tone={statusTone(item.computedStatus)}>
                          {item.computedStatus || (item.isActive ? "ACTIVE" : "INACTIVE")}
                        </Pill>
                      </td>

                      <td className="px-2 py-2.5">
                        <div className="flex flex-wrap items-center justify-center gap-1.5 [&>button]:shrink-0">
                          <button
                            type="button"
                            onClick={() => setPreviewOpen(item)}
                            className="rounded-[10px] border p-2 text-slate-500 transition hover:bg-[#fff4f6] hover:text-[#d86a78]"
                            style={{ borderColor: "#e7eaf0" }}
                            title="Preview video"
                          >
                            <PlaySquare size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openStats(item)}
                            className="rounded-[10px] border p-2 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]"
                            style={{ borderColor: "#e7eaf0" }}
                            title="Stats"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(item)}
                            className="rounded-[10px] border p-2 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]"
                            style={{ borderColor: "#e7eaf0" }}
                            title="Edit"
                          >
                            <Edit3 size={16} />
                          </button>
                          {!item.deletedAt ? (
                            <button
                              type="button"
                              onClick={() => toggleStatus(item)}
                              className="rounded-[10px] border p-2 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]"
                              style={{ borderColor: "#e7eaf0" }}
                              title="Activate/deactivate"
                            >
                              <Power size={16} />
                            </button>
                          ) : null}
                          {!item.deletedAt ? (
                            <button
                              type="button"
                              onClick={() => {
                                setDeleteTarget(item);
                                setDeleteError("");
                              }}
                              className="rounded-[10px] border p-2 text-red-500 transition hover:bg-red-50"
                              style={{ borderColor: "#f1d1d5" }}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white">
            <div className="flex flex-col gap-3 border-b px-5 py-4 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: "#eef2f7" }}>
              <div>
                <div className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <History size={18} className="text-[#d86a78]" />
                  History timeline
                </div>
                <div className="mt-1 text-xs leading-5 text-slate-500">
                  Create, update, delete, activate, and deactivate logs. Delete rows only when you really do not need the audit trail.
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="pink">{history.length} rows</Pill>
                {confirmingClearHistory ? (
                  <button
                    type="button"
                    disabled={historyDeleting}
                    onClick={() => {
                      setConfirmingClearHistory(false);
                      setHistoryDeleteError("");
                    }}
                    className="inline-flex h-[40px] items-center justify-center rounded-[14px] border bg-white px-4 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ borderColor: "#e7eaf0" }}
                  >
                    Cancel
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={historyDeleting || history.length === 0}
                  onClick={clearHistory}
                  className={`inline-flex h-[40px] items-center justify-center gap-2 rounded-[14px] px-4 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-60 ${confirmingClearHistory ? "bg-red-500 text-white hover:bg-red-600" : "border bg-white text-red-500 hover:bg-red-50"}`}
                  style={confirmingClearHistory ? undefined : { borderColor: "#f1d1d5" }}
                >
                  {historyDeleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  {confirmingClearHistory ? "Confirm clear all" : "Clear history"}
                </button>
              </div>
            </div>

            {historyDeleteError ? (
              <div className="px-5 pt-4">
                <ErrorNotice message={historyDeleteError} onClose={() => setHistoryDeleteError("")} />
              </div>
            ) : null}

            {history.length ? (
              <div className="max-h-[620px] overflow-auto px-5 py-4">
                <div className="space-y-3">
                  {history.map((row) => {
                    const confirmRowDelete = String(historyRowConfirmId) === String(row.id);
                    const action = String(row.action || "LOG").toUpperCase();
                    const actionTone = action === "DELETE" ? "red" : action === "CREATE" ? "green" : action === "UPDATE" ? "blue" : "slate";

                    return (
                      <div
                        key={row.id}
                        className="rounded-[20px] border bg-[#fcfcfd] px-4 py-4 transition hover:bg-white hover:shadow-[0_12px_35px_rgba(15,23,42,0.05)]"
                        style={{ borderColor: "#eef2f7" }}
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#fff4f6] text-[#d86a78]">
                                <History size={16} />
                              </span>
                              <Pill tone={actionTone}>{action}</Pill>
                              <div className="min-w-0 truncate text-sm font-bold text-slate-900">
                                {row.short_video_title || row.title || "Deleted video"}
                              </div>
                            </div>

                            <div className="mt-3 grid gap-2 text-xs text-slate-500 sm:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-[14px] bg-white px-3 py-2">
                                <span className="font-bold text-slate-400">Actor:</span> {row.actor_name || row.actor_email || row.actor_id || "System"}
                              </div>
                              <div className="rounded-[14px] bg-white px-3 py-2">
                                <span className="font-bold text-slate-400">Video ID:</span> {row.short_video_id || "-"}
                              </div>
                              <div className="rounded-[14px] bg-white px-3 py-2">
                                <span className="font-bold text-slate-400">Date:</span> {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
                              </div>
                              <div className="rounded-[14px] bg-white px-3 py-2">
                                <span className="font-bold text-slate-400">Reason:</span> {row.reason || "-"}
                              </div>
                            </div>
                          </div>

                          <div className="flex shrink-0 items-center justify-end gap-2">
                            {confirmRowDelete ? (
                              <button
                                type="button"
                                disabled={historyDeleting}
                                onClick={() => {
                                  setHistoryRowConfirmId(null);
                                  setHistoryDeleteError("");
                                }}
                                className="inline-flex h-[40px] items-center justify-center rounded-[14px] border bg-white px-4 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                style={{ borderColor: "#e7eaf0" }}
                              >
                                Cancel
                              </button>
                            ) : null}
                            <button
                              type="button"
                              disabled={historyDeleting}
                              onClick={() => deleteHistoryRow(row)}
                              className={`inline-flex h-[40px] items-center justify-center gap-2 rounded-[14px] px-4 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-60 ${confirmRowDelete ? "bg-red-500 text-white hover:bg-red-600" : "border bg-white text-red-500 hover:bg-red-50"}`}
                              style={confirmRowDelete ? undefined : { borderColor: "#f1d1d5" }}
                              title="Delete this history row"
                            >
                              {historyDeleting && confirmRowDelete ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                              {confirmRowDelete ? "Confirm delete" : "Delete"}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="px-5 py-14 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#fff4f6] text-[#d86a78]">
                  <History size={24} />
                </div>
                <div className="mt-4 text-base font-bold text-slate-900">No history yet</div>
                <div className="mt-1 text-sm text-slate-500">Create or update a short video and the audit trail will appear here.</div>
              </div>
            )}
          </div>
        )}

        {tab === "videos" ? (
          <div
            className="flex flex-col gap-3 border-t bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5"
            style={{ borderColor: "#eef2f7" }}
          >
            <div className="text-sm font-medium text-slate-500">
              Showing {visiblePageStart}-{visiblePageEnd} of {visibleTotal} videos
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-500">Rows</span>
                <InlineSelect
                  value={String(limit)}
                  minWidth={88}
                  options={PAGE_SIZE_OPTIONS}
                  onChange={handlePageSizeChange}
                  menuPlacement="auto"
                  menuMaxHeight={220}
                />
              </div>

              <button
                type="button"
                disabled={page <= 1}
                onClick={() => loadVideos(page - 1)}
                className="inline-flex h-[42px] items-center justify-center rounded-[14px] border bg-white px-5 text-sm font-semibold text-slate-500 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: "#e7eaf0" }}
              >
                Prev
              </button>

              <div className="min-w-[78px] text-center text-sm font-semibold text-slate-500">
                Page {page} / {visibleTotalPages}
              </div>

              <button
                type="button"
                disabled={page >= visibleTotalPages}
                onClick={() => loadVideos(page + 1)}
                className="inline-flex h-[42px] items-center justify-center rounded-[14px] border bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: "#f2c6cc" }}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </Card>

      <Modal
        open={modalOpen}
        title={editing ? "Edit short video" : "Create New Short Video"}
        subtitle="Add content, targeting, schedule, and video details for the short video module."
        onClose={closeEditor}
      >
        <VideoForm
          form={form}
          setForm={setForm}
          filterOptions={filterOptions}
          editing={editing}
          saving={saving}
          conflicts={conflicts}
          error={error}
          onDismissError={() => setError("")}
          onCheckConflicts={checkConflicts}
          onSubmit={handleSubmit}
          onClose={closeEditor}
        />
      </Modal>

      <VideoPreviewModal item={previewOpen} onClose={() => setPreviewOpen(null)} />

      <DeleteConfirmModal
        item={deleteTarget}
        deleting={deleting}
        error={deleteError}
        onDismissError={() => setDeleteError("")}
        onClose={() => {
          if (deleting) return;
          setDeleteTarget(null);
          setDeleteError("");
        }}
        onConfirm={() => removeVideo(deleteTarget)}
      />

    </div>
  );
}
